<template>
  <div class="extra-schedule" :class="{ 'has-error': hasError }">
    <IconBoxWrapper icon="icon-f-clock" :hide-icon="hideIcon" :icon-color="hasError ? 'white' : null" :icon-background="hasError ? 'negative' : null">
      <div class="extra-schedule-content">
        <section>
          <QSelect
            ref="scheduleStart"
            class="q-select-no-arrow"
            :model-value="startTime"
            :error="hasError"
            stack-label
            emit-value
            map-options
            :disable="isReadonly"
            no-error-icon
            :display-value="!startTime && startTime !== 0 ? 'hh:mm' : void 0"
            :label="$t('CORE.COMPONENTS.SCHEDULE.@START_LABEL')"
            :options="scheduleOptions.start"
            @update:model-value="$emit('selectStart', $event)"
            @popup-show="scrollTo(SCROLL_TO_INDEX, 'scheduleStart', startTime)"
          >
            <template #option="scope">
              <QItem v-bind="scope.itemProps">
                <QItemSection>
                  <QItemLabel>{{ scope.opt.label }}</QItemLabel>
                  <QItemLabel caption>
                    {{ scope.opt.stamp }}
                  </QItemLabel>
                </QItemSection>
              </QItem>
            </template>
          </QSelect>
          <p class="extra-schedule-subtitle">
            {{ startTime >= DAY_DURATION_MINUTES ? $t("CORE.COMPONENTS.SCHEDULE.@DAY_PLUS_ONE") : "&nbsp;" }}
          </p>
        </section>
        <section>
          <QSelect
            ref="scheduleEnd"
            class="q-select-no-arrow"
            :model-value="endTime"
            :error="hasError"
            stack-label
            emit-value
            map-options
            :disable="isReadonly"
            no-error-icon
            :display-value="!endTime && endTime !== 0 ? 'hh:mm' : void 0"
            :label="$t('CORE.COMPONENTS.SCHEDULE.@END_LABEL')"
            :options="endOptions"
            @filter="lazyLoadEndOptions"
            @update:model-value="$emit('selectEnd', $event)"
            @popup-show="scrollTo(SCROLL_TO_INDEX, 'scheduleEnd', startTime)"
          >
            <template #option="scope">
              <QItem v-bind="scope.itemProps">
                <QItemSection>
                  <QItemLabel>{{ scope.opt.label }}</QItemLabel>
                  <QItemLabel caption>
                    {{ scope.opt.stamp }}
                  </QItemLabel>
                </QItemSection>
              </QItem>
            </template>
          </QSelect>
          <p class="extra-schedule-subtitle">
            {{ endTime >= DAY_DURATION_MINUTES ? $t("CORE.COMPONENTS.SCHEDULE.@DAY_PLUS_ONE") : "&nbsp;" }}
          </p>
        </section>
        <section v-if="allowBreak" class="extra-schedule-break">
          <QSelect
            class="q-select-no-arrow"
            :model-value="breakTimeDuration"
            :error="hasError"
            stack-label
            emit-value
            map-options
            :disable="isReadonly"
            no-error-icon
            :label="$t('CORE.COMPONENTS.SCHEDULE.@BREAK_LABEL')"
            :display-value="breakTimeDurationDisplayValue"
            :options="scheduleOptions.breakDuration"
            @update:model-value="$emit('selectBreakDuration', $event)"
          />
        </section>
      </div>
    </IconBoxWrapper>
    <p v-if="endBeforeStartError" class="extra-schedule-error-subtitle">
      {{ $t("CORE.COMPONENTS.SCHEDULE.@END_BEFORE_START") }}
    </p>
    <p v-if="!endBeforeStartError && breakTooLongError" class="extra-schedule-error-subtitle">
      {{ $t("CORE.COMPONENTS.SCHEDULE.@BREAK_TOO_LONG", { duration: maxBreakDurationDisplay }) }}
    </p>
    <p v-if="!endBeforeStartError && !breakTooLongError && scheduleNotWorkingToday" class="extra-schedule-error-subtitle">
      {{ $t("CORE.COMPONENTS.SCHEDULE.@SCHEDULE_NOT_WORKING_TODAY") }}
    </p>
  </div>
</template>
<script>
  import { DatesHelpers, ScheduleHelpers } from "@core/helpers";

  const SCROLL_TO_INDEX = 8 * 4; // 8 hours by number of options for each hours

  export default {
    props: {
      isReadonly: { type: Boolean, default: false },
      hideIcon: { type: Boolean, default: false },
      startTime: { type: Number, default: null },
      endTime: { type: Number, default: null },
      breakTimeDuration: { type: Number, default: null },
      isToday: { type: Boolean, default: false },
      allowBreak: { type: Boolean, default: true },
      scheduleOptions: {
        type: Object,
        default: () => ScheduleHelpers.getFullScheduleOptions(),
      },
    },
    emits: ["selectStart", "selectEnd", "selectBreakDuration", "onError"],
    data() {
      return {
        endOptions: [],
      };
    },
    computed: {
      maxBreakDuration() {
        return (this.endTime - this.startTime) / 2;
      },
      maxBreakDurationDisplay() {
        const nearestStep = this.maxBreakDuration % ScheduleHelpers.STEP;
        return DatesHelpers.formatMinsToHrsMins(this.maxBreakDuration - nearestStep);
      },
      endBeforeStartError() {
        return (
          this.startTime >= 0 && //
          this.endTime > 0 &&
          this.endTime < this.startTime + ScheduleHelpers.MINIMAL_SHIFT_DURATION * 60
        );
      },
      breakTooLongError() {
        return (
          this.startTime >= 0 && //
          this.endTime > 0 &&
          this.breakTimeDuration > this.maxBreakDuration
        );
      },
      hasError() {
        return this.endBeforeStartError || this.breakTooLongError || this.scheduleNotWorkingToday;
      },
      scheduleNotWorkingToday() {
        return this.isToday && this.startTime && this.startTime <= DatesHelpers.getDayjs().hour() * 60;
      },
      breakTimeDurationDisplayValue() {
        if (this.breakTimeDuration !== undefined && this.breakTimeDuration !== null) {
          return undefined;
        }
        return this.$t("CORE.COMPONENTS.SCHEDULE.@BREAK_PLACEHOLDER");
      },
    },
    watch: {
      hasError(value) {
        this.$emit("onError", value);
      },
    },
    created() {
      // constants
      this.DAY_DURATION_MINUTES = ScheduleHelpers.DAY_DURATION_MINUTES;
      this.SCROLL_TO_INDEX = SCROLL_TO_INDEX;
    },
    mounted() {
      this.ensureOptionsMapping();
    },
    methods: {
      scrollTo(index, componentRef, value) {
        if (!value) {
          const ref = this.$refs[componentRef];
          setTimeout(() => {
            ref.scrollTo(index);
          }, 100);
        }
      },
      ensureOptionsMapping() {
        this.endOptions = [...this.scheduleOptions.end];
        const selectedStartOption = this.scheduleOptions.start.find((option) => option.value === this.startTime);
        if (!selectedStartOption) {
          // selected value is outside of available range (for instance it's before now)
          this.$emit("selectStart", undefined);
        }

        const selectedEndOption = this.scheduleOptions.end.find((option) => option.value === this.endTime);
        if (!selectedEndOption) {
          // selected value is not available (for instance it's before start value)
          this.$emit("selectEnd", undefined);
        }
      },
      lazyLoadEndOptions(val, update, abort) {
        const endOptions = [...this.scheduleOptions.end];

        update(() => {
          // we filter endOptions in order to display only the relevant ones
          this.endOptions = endOptions.filter((option) => option.value >= this.startTime + ScheduleHelpers.MINIMAL_SHIFT_DURATION * 60);
        });
      },
    },
  };
</script>

<style lang="scss">
  .extra-schedule {
    .extra-schedule-content {
      padding: 0 10px;
      display: flex;

      > section {
        margin: 0 5px;
        flex: 1;

        &.extra-schedule-break {
          flex: 2;
        }

        .extra-schedule-subtitle {
          margin: 3px 0 5px;
          font-size: 0.75em;
          color: $primary-light;
          height: 1em;
        }

        .q-field,
        .q-field__native {
          padding-bottom: 0; // override quasar - hack to remove white space around field
        }
      }
    }

    .extra-schedule-error-subtitle {
      margin: 5px 0;
      color: $negative;
      font-size: 0.75em;
    }

    &.has-error {
      .extra-icon-box-wrapper {
        border-color: $negative;
      }
    }
  }
</style>
